import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import api from '@/lib/api';

const CATEGORIES = ['sermons', 'worship', 'programmes', 'events'];

export default function FwtvPremiumAdmin() {
  const qc = useQueryClient();
  const [imp, setImp] = useState({ url: '', title: '', category: 'sermons', is_premium: false });
  const [live, setLive] = useState({ title: '', start_at: '', duration_minutes: 120, video_url: '' });
  const [msg, setMsg] = useState({ subject: '', message: '', audience: 'active' });
  const [settings, setSettings] = useState(null);

  const { data: saved } = useQuery({
    queryKey: ['fwtv-tv-settings'],
    queryFn: () => api.get('/fwtv/admin/tv/settings').then((r) => {
      setSettings(r.data);
      return r.data;
    }),
  });
  const { data: videos } = useQuery({
    queryKey: ['fwtv-admin-videos'],
    queryFn: () => api.get('/fwtv/admin/tv/videos?limit=100').then((r) => r.data.videos),
  });
  const { data: subs } = useQuery({
    queryKey: ['fwtv-subscribers'],
    queryFn: () => api.get('/fwtv/admin/tv/subscribers').then((r) => r.data),
  });
  const { data: streams } = useQuery({
    queryKey: ['fwtv-live-schedule'],
    queryFn: () => api.get('/fwtv/admin/tv/live/schedule').then((r) => r.data.streams),
  });

  const cfg = settings || saved;
  const setSection = (section, key, value) =>
    setSettings((prev) => ({ ...(prev || saved), [section]: { ...((prev || saved)?.[section] || {}), [key]: value } }));

  const saveSettings = useMutation({
    mutationFn: () => api.put('/fwtv/admin/tv/settings',
      { youtube: cfg?.youtube, premium: cfg?.premium }).then((r) => r.data),
    onSuccess: () => { toast.success('Settings saved'); qc.invalidateQueries({ queryKey: ['fwtv-tv-settings'] }); },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not save'),
  });

  const syncYouTube = useMutation({
    mutationFn: () => api.post('/fwtv/admin/tv/youtube/sync').then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`YouTube: ${d.imported} new, ${d.updated} updated, ${d.live} live`);
      qc.invalidateQueries({ queryKey: ['fwtv-admin-videos'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Sync failed'),
  });

  const importVideo = useMutation({
    mutationFn: () => api.post('/fwtv/admin/tv/videos/import', imp).then((r) => r.data),
    onSuccess: () => {
      toast.success('Video added');
      setImp({ url: '', title: '', category: 'sermons', is_premium: false });
      qc.invalidateQueries({ queryKey: ['fwtv-admin-videos'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Import failed'),
  });

  const togglePremium = useMutation({
    mutationFn: ({ id, is_premium }) =>
      api.patch(`/fwtv/admin/tv/videos/${id}/premium`, { is_premium }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['fwtv-admin-videos'] }),
  });

  const scheduleLive = useMutation({
    mutationFn: () => api.post('/fwtv/admin/tv/live/schedule', live).then((r) => r.data),
    onSuccess: () => {
      toast.success('Live stream scheduled');
      setLive({ title: '', start_at: '', duration_minutes: 120, video_url: '' });
      qc.invalidateQueries({ queryKey: ['fwtv-live-schedule'] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Could not schedule'),
  });

  const broadcast = useMutation({
    mutationFn: () => api.post('/fwtv/admin/tv/broadcast', msg).then((r) => r.data),
    onSuccess: (d) => {
      toast.success(`${d.recipients} subscribers · ${d.sent} sent, ${d.queued} queued`);
      setMsg({ subject: '', message: '', audience: 'active' });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || 'Broadcast failed'),
  });

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6" data-testid="fwtv-premium-admin">
      <div>
        <h1 className="text-2xl font-bold">Faith Works TV</h1>
        <p className="text-sm text-slate-500">Content, YouTube pull, Premium subscribers and broadcasts.</p>
      </div>

      <Tabs defaultValue="content">
        <TabsList>
          <TabsTrigger value="content" data-testid="fwtv-tab-content">Content</TabsTrigger>
          <TabsTrigger value="live" data-testid="fwtv-tab-live">Live</TabsTrigger>
          <TabsTrigger value="subscribers" data-testid="fwtv-tab-subs">Subscribers</TabsTrigger>
          <TabsTrigger value="settings" data-testid="fwtv-tab-settings">Settings</TabsTrigger>
        </TabsList>

        <TabsContent value="content" className="space-y-6 mt-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Add a video</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>YouTube / Vimeo / file URL</Label>
                <Input value={imp.url} onChange={(e) => setImp({ ...imp, url: e.target.value })}
                       placeholder="https://youtu.be/…" data-testid="fwtv-import-url" />
              </div>
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={imp.title} onChange={(e) => setImp({ ...imp, title: e.target.value })}
                       data-testid="fwtv-import-title" />
              </div>
              <div className="space-y-2">
                <Label>Category</Label>
                <div className="flex flex-wrap gap-2">
                  {CATEGORIES.map((c) => (
                    <Button key={c} type="button" size="sm"
                            variant={imp.category === c ? 'default' : 'outline'}
                            data-testid={`fwtv-import-cat-${c}`}
                            onClick={() => setImp({ ...imp, category: c })}
                            className="capitalize">{c}</Button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <div>
                  <Label className="font-medium">Premium only</Label>
                  <p className="text-xs text-slate-500">Public users see the thumbnail, not the video</p>
                </div>
                <Switch checked={imp.is_premium} data-testid="fwtv-import-premium"
                        onCheckedChange={(v) => setImp({ ...imp, is_premium: v })} />
              </div>
              <div className="md:col-span-2 flex gap-2">
                <Button onClick={() => importVideo.mutate()}
                        disabled={!imp.url || !imp.title || importVideo.isPending}
                        data-testid="fwtv-import-submit">
                  {importVideo.isPending ? 'Adding…' : 'Add video'}
                </Button>
                <Button variant="outline" onClick={() => syncYouTube.mutate()}
                        disabled={syncYouTube.isPending} data-testid="fwtv-youtube-sync">
                  {syncYouTube.isPending ? 'Syncing…' : 'Pull from YouTube now'}
                </Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader><CardTitle className="text-base">Library ({videos?.length || 0})</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[520px] overflow-y-auto">
              {(videos || []).map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-4 border-b py-2 last:border-0"
                     data-testid={`fwtv-video-row-${v.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{v.title}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {v.category || 'sermons'} · {v.source || 'manual'} · {v.status}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 text-xs shrink-0">
                    Premium
                    <Switch checked={!!v.is_premium} data-testid={`fwtv-premium-toggle-${v.id}`}
                            onCheckedChange={(val) => togglePremium.mutate({ id: v.id, is_premium: val })} />
                  </label>
                </div>
              ))}
              {!videos?.length && <p className="text-sm text-slate-500">No videos yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="live" className="space-y-6 mt-6">
          <Card>
            <CardHeader><CardTitle className="text-base">Schedule a live stream</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input value={live.title} onChange={(e) => setLive({ ...live, title: e.target.value })}
                       data-testid="fwtv-live-title" />
              </div>
              <div className="space-y-2">
                <Label>Starts at</Label>
                <Input type="datetime-local" value={live.start_at}
                       onChange={(e) => setLive({ ...live, start_at: e.target.value })}
                       data-testid="fwtv-live-start" />
              </div>
              <div className="space-y-2">
                <Label>Stream URL</Label>
                <Input value={live.video_url} onChange={(e) => setLive({ ...live, video_url: e.target.value })}
                       placeholder="https://youtube.com/live/…" data-testid="fwtv-live-url" />
              </div>
              <div className="space-y-2">
                <Label>Duration (minutes)</Label>
                <Input type="number" value={live.duration_minutes}
                       onChange={(e) => setLive({ ...live, duration_minutes: Number(e.target.value) })}
                       data-testid="fwtv-live-duration" />
              </div>
              <div className="md:col-span-2">
                <Button onClick={() => scheduleLive.mutate()}
                        disabled={!live.title || !live.start_at || scheduleLive.isPending}
                        data-testid="fwtv-live-submit">Schedule</Button>
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Scheduled</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              {(streams || []).map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b py-2 last:border-0">
                  <div>
                    <p className="text-sm font-medium">{s.title}</p>
                    <p className="text-xs text-slate-500">{new Date(s.start_at).toLocaleString()}</p>
                  </div>
                  <Button variant="ghost" size="sm" data-testid={`fwtv-live-delete-${s.id}`}
                          onClick={() => api.delete(`/fwtv/admin/tv/live/schedule/${s.id}`)
                            .then(() => qc.invalidateQueries({ queryKey: ['fwtv-live-schedule'] }))}>
                    Remove
                  </Button>
                </div>
              ))}
              {!streams?.length && <p className="text-sm text-slate-500">Nothing scheduled.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="subscribers" className="space-y-6 mt-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="fwtv-sub-stats">
            {[['Active', subs?.summary?.active], ['Pending', subs?.summary?.pending],
              ['Monthly', subs?.summary?.monthly], ['Annual', subs?.summary?.annual]].map(([k, v]) => (
              <Card key={k}><CardContent className="p-4">
                <p className="text-xs uppercase tracking-wider text-slate-500">{k}</p>
                <p className="text-2xl font-semibold">{v ?? 0}</p>
              </CardContent></Card>
            ))}
          </div>
          <Card>
            <CardHeader><CardTitle className="text-base">Revenue</CardTitle></CardHeader>
            <CardContent className="flex flex-wrap gap-6">
              {Object.entries(subs?.summary?.revenue_by_currency || {}).map(([cur, amt]) => (
                <div key={cur}>
                  <p className="text-xs text-slate-500">{cur}</p>
                  <p className="text-xl font-semibold">{amt.toLocaleString()}</p>
                </div>
              ))}
              {!Object.keys(subs?.summary?.revenue_by_currency || {}).length && (
                <p className="text-sm text-slate-500">No subscription revenue yet.</p>
              )}
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Broadcast to subscribers</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <Input value={msg.subject} onChange={(e) => setMsg({ ...msg, subject: e.target.value })}
                     placeholder="Subject" data-testid="fwtv-broadcast-subject" />
              <Textarea rows={4} value={msg.message} onChange={(e) => setMsg({ ...msg, message: e.target.value })}
                        placeholder="Your message to Premium subscribers"
                        data-testid="fwtv-broadcast-message" />
              <Button onClick={() => broadcast.mutate()} disabled={!msg.subject || !msg.message || broadcast.isPending}
                      data-testid="fwtv-broadcast-send">
                {broadcast.isPending ? 'Sending…' : 'Send broadcast'}
              </Button>
              <p className="text-xs text-amber-600">
                While email is switched off, broadcasts are held in the outbox (System Settings →
                Email) and go out the moment mail credentials are saved.
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Subscribers</CardTitle></CardHeader>
            <CardContent className="space-y-2 max-h-[400px] overflow-y-auto">
              {(subs?.subscribers || []).map((s) => (
                <div key={s.id} className="flex items-center justify-between border-b py-2 last:border-0 text-sm">
                  <span>{s.email || s.user_id}</span>
                  <span className="text-slate-500">
                    {s.plan} · {s.status} · {s.currency} {s.amount}
                  </span>
                </div>
              ))}
              {!subs?.subscribers?.length && <p className="text-sm text-slate-500">No subscribers yet.</p>}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="settings" className="space-y-6 mt-6">
          <Card>
            <CardHeader><CardTitle className="text-base">YouTube channel</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Channel ID</Label>
                <Input value={cfg?.youtube?.channel_id || ''} data-testid="fwtv-yt-channel"
                       onChange={(e) => setSection('youtube', 'channel_id', e.target.value)}
                       placeholder="UCxxxxxxxxxxxxxxxx" />
              </div>
              <div className="space-y-2">
                <Label>YouTube Data API key</Label>
                <Input value={cfg?.youtube?.api_key || ''} data-testid="fwtv-yt-key"
                       onChange={(e) => setSection('youtube', 'api_key', e.target.value)}
                       placeholder="AIza…" />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3 md:col-span-2">
                <div>
                  <Label className="font-medium">Pull automatically</Label>
                  <p className="text-xs text-slate-500">
                    Latest uploads and live streams sync in at most every 15 minutes.
                    {cfg?.youtube?.last_sync_result ? ` Last run: ${cfg.youtube.last_sync_result}` : ''}
                  </p>
                </div>
                <Switch checked={!!cfg?.youtube?.auto_sync} data-testid="fwtv-yt-auto"
                        onCheckedChange={(v) => setSection('youtube', 'auto_sync', v)} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Premium plans</CardTitle></CardHeader>
            <CardContent className="grid md:grid-cols-4 gap-4">
              <div className="space-y-2">
                <Label>Currency</Label>
                <Input value={cfg?.premium?.currency || ''} data-testid="fwtv-price-currency"
                       onChange={(e) => setSection('premium', 'currency', e.target.value.toUpperCase())} />
              </div>
              <div className="space-y-2">
                <Label>Monthly</Label>
                <Input type="number" value={cfg?.premium?.monthly_price ?? ''} data-testid="fwtv-price-monthly"
                       onChange={(e) => setSection('premium', 'monthly_price', Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>Annual</Label>
                <Input type="number" value={cfg?.premium?.annual_price ?? ''} data-testid="fwtv-price-annual"
                       onChange={(e) => setSection('premium', 'annual_price', Number(e.target.value))} />
              </div>
              <div className="space-y-2">
                <Label>Trial days</Label>
                <Input type="number" value={cfg?.premium?.trial_days ?? ''} data-testid="fwtv-trial-days"
                       onChange={(e) => setSection('premium', 'trial_days', Number(e.target.value))} />
              </div>
              <div className="md:col-span-4 space-y-2">
                <Label>Blurb</Label>
                <Input value={cfg?.premium?.blurb || ''} data-testid="fwtv-blurb"
                       onChange={(e) => setSection('premium', 'blurb', e.target.value)} />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader><CardTitle className="text-base">Embed on faithworldministries.com</CardTitle></CardHeader>
            <CardContent className="space-y-2">
              <p className="text-sm text-slate-600">Paste this on /faith-works-tv:</p>
              <pre className="text-xs bg-slate-900 text-slate-100 p-3 rounded-lg overflow-x-auto"
                   data-testid="fwtv-embed-snippet">{`<div data-fwb="faith-works-tv"></div>
<script src="${process.env.REACT_APP_BACKEND_URL}/api/fwtv/public/tv.js" defer></script>`}</pre>
            </CardContent>
          </Card>
          <Button onClick={() => saveSettings.mutate()} disabled={saveSettings.isPending}
                  data-testid="fwtv-settings-save">
            {saveSettings.isPending ? 'Saving…' : 'Save settings'}
          </Button>
        </TabsContent>
      </Tabs>
    </div>
  );
}
